import {
  DEFAULT_UNITS,
  ELECTRIC_RATE_UNITS,
  ELECTRIC_USAGE_UNITS,
  GAS_UNIT_CATALOG,
  INDUSTRY_UNITS,
  MAX_UNIT_PRECISION,
} from "@/types/generated/unit-catalog";
import type { ElectricUsageUnit } from "@/types/generated/ElectricUsageUnit";
import type { GasUnit } from "@/types/generated/GasUnit";
import type { PricingUnit } from "@/types/generated/PricingUnit";
import type { UserPreferences } from "@/types/users";
import type { Commodity } from "@/types/utilities";

/**
 * Units — the one way the SPA shows an electric or gas rate, price, adder or
 * usage.
 *
 * **Every rate and usage on screen goes through here** — `useUnits()` in a
 * component, or the explicit-units functions below where there is no React
 * (a chart formatter, a CSV builder). Storage is canonical: electric rates
 * `$/kWh` and usage kWh, gas rates `$/therm` and usage therms. These convert
 * to the reader's units (Preferences → Units: a unit and 0–5 decimals for each
 * of electric rate, electric usage, gas rate and gas usage) and round half away
 * from zero, as the server's `pilot_domain::units` does.
 *
 * Never append `/kWh`, hand-roll a `toFixed` on a rate, or pick a unit on one
 * screen: `scripts/check-units.sh` fails the build on it. The conversion
 * factors and labels come from `types/generated/unit-catalog.ts`, emitted from
 * Rust, so client and server agree.
 */

/** A reader's units, resolved: each unit with its decimal places. */
export interface UnitPrefs {
  electricRate: PricingUnit;
  electricRatePrecision: number;
  electricUsage: ElectricUsageUnit;
  electricUsagePrecision: number;
  gasRate: GasUnit;
  gasRatePrecision: number;
  gasUsage: GasUnit;
  gasUsagePrecision: number;
}

export { MAX_UNIT_PRECISION, ELECTRIC_RATE_UNITS, ELECTRIC_USAGE_UNITS, GAS_UNIT_CATALOG };

/** Every precision a reader may pick, 0 (whole units) to the maximum. */
export const UNIT_PRECISIONS: readonly number[] = Array.from(
  { length: MAX_UNIT_PRECISION + 1 },
  (_, i) => i,
);

const electricRateSpec = (u: PricingUnit) =>
  ELECTRIC_RATE_UNITS.find((s) => s.code === u) ?? ELECTRIC_RATE_UNITS[0];
const electricUsageSpec = (u: ElectricUsageUnit) =>
  ELECTRIC_USAGE_UNITS.find((s) => s.code === u) ?? ELECTRIC_USAGE_UNITS[0];
const gasSpec = (u: GasUnit) => GAS_UNIT_CATALOG.find((s) => s.code === u) ?? GAS_UNIT_CATALOG[0];

/** Decimals a unit reads naturally at — what a reader who never chose gets. */
export function defaultElectricRatePrecision(u: PricingUnit): number {
  return electricRateSpec(u).defaultPrecision;
}
export function defaultElectricUsagePrecision(u: ElectricUsageUnit): number {
  return electricUsageSpec(u).defaultPrecision;
}
export function defaultGasRatePrecision(u: GasUnit): number {
  return gasSpec(u).defaultRatePrecision;
}
export function defaultGasUsagePrecision(u: GasUnit): number {
  return gasSpec(u).defaultUsagePrecision;
}

const clampPrecision = (p: number | null | undefined, fallback: number) =>
  Math.min(MAX_UNIT_PRECISION, Math.max(0, p ?? fallback));

/** The unit preferences a person has stored. */
export type StoredUnitPreferences = Pick<
  UserPreferences,
  | "electric_rate_unit"
  | "electric_rate_precision"
  | "electric_usage_unit"
  | "electric_usage_precision"
  | "gas_rate_unit"
  | "gas_rate_precision"
  | "gas_usage_unit"
  | "gas_usage_precision"
>;

/** Every preference key that changes how a rate or usage reads. */
export const UNIT_PREFERENCE_KEYS: readonly (keyof StoredUnitPreferences)[] = [
  "electric_rate_unit",
  "electric_rate_precision",
  "electric_usage_unit",
  "electric_usage_precision",
  "gas_rate_unit",
  "gas_rate_precision",
  "gas_usage_unit",
  "gas_usage_precision",
];

/**
 * A person's units: each one they chose, the default for the rest. An
 * unchosen precision follows its unit, so a unit change alone never strands a
 * precision that suited another unit. Mirrors `UnitPrefs::from_preferences`.
 */
export function resolveUnitPrefs(stored: Partial<StoredUnitPreferences>): UnitPrefs {
  const electricRate = stored.electric_rate_unit ?? DEFAULT_UNITS.electricRate;
  const electricUsage = stored.electric_usage_unit ?? DEFAULT_UNITS.electricUsage;
  const gasRate = stored.gas_rate_unit ?? DEFAULT_UNITS.gasRate;
  const gasUsage = stored.gas_usage_unit ?? DEFAULT_UNITS.gasUsage;
  return {
    electricRate,
    electricRatePrecision: clampPrecision(
      stored.electric_rate_precision,
      defaultElectricRatePrecision(electricRate),
    ),
    electricUsage,
    electricUsagePrecision: clampPrecision(
      stored.electric_usage_precision,
      defaultElectricUsagePrecision(electricUsage),
    ),
    gasRate,
    gasRatePrecision: clampPrecision(stored.gas_rate_precision, defaultGasRatePrecision(gasRate)),
    gasUsage,
    gasUsagePrecision: clampPrecision(stored.gas_usage_precision, defaultGasUsagePrecision(gasUsage)),
  };
}

/** What a reader who has never chosen gets. */
export const DEFAULT_UNIT_PREFS: UnitPrefs = resolveUnitPrefs({});

/** The supplier convention — for anything a supplier reads. */
export const INDUSTRY_UNIT_PREFS: UnitPrefs = {
  electricRate: INDUSTRY_UNITS.electricRate,
  electricRatePrecision: INDUSTRY_UNITS.electricRatePrecision,
  electricUsage: INDUSTRY_UNITS.electricUsage,
  electricUsagePrecision: INDUSTRY_UNITS.electricUsagePrecision,
  gasRate: INDUSTRY_UNITS.gasRate,
  gasRatePrecision: INDUSTRY_UNITS.gasRatePrecision,
  gasUsage: INDUSTRY_UNITS.gasUsage,
  gasUsagePrecision: INDUSTRY_UNITS.gasUsagePrecision,
};

type Numeric = number | string | null | undefined;

/**
 * A wire value as a number, or `null`. Tolerates stringified `Decimal`s, and
 * — unlike a bare `Number()` — treats a blank string as no value rather than
 * zero.
 */
function toNumber(v: Numeric): number | null {
  if (v == null) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const isElectric = (c: Commodity) => c === "electric";
const isGas = (c: Commodity) => c === "natural_gas";

/**
 * `n` rounded half away from zero to `dp` places. Shifting through the
 * exponent in the string form (rather than multiplying) keeps `1.005` from
 * rounding down as its binary double would.
 */
export function roundHalfAway(n: number, dp: number): number {
  const sign = n < 0 ? -1 : 1;
  const abs = String(Math.abs(n));
  // A value too small or large for plain notation (`1e-7`) can't take the
  // shift; at those magnitudes the binary residue is far below any precision.
  if (abs.includes("e")) return sign * Number(Math.abs(n).toFixed(dp));
  const shifted = Math.round(Number(`${abs}e${dp}`));
  return sign * Number(`${shifted}e-${dp}`);
}

const groupers = new Map<number, Intl.NumberFormat>();
function grouped(n: number, dp: number): string {
  let f = groupers.get(dp);
  if (!f) {
    f = new Intl.NumberFormat("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
    groupers.set(dp, f);
  }
  const rounded = roundHalfAway(n, dp);
  // `-0` would print as "-0.00"; a value that rounds to zero carries no sign.
  return f.format(rounded === 0 ? 0 : rounded);
}

function dollars(n: number, dp: number): string {
  const text = grouped(n, dp);
  return text.startsWith("-") ? `-$${text.slice(1)}` : `$${text}`;
}

// ── Rates ─────────────────────────────────────────────────────────────────

/** A canonical rate in the reader's rate unit, unrounded — for a chart axis or a sort. */
export function rateValue(commodity: Commodity, canonical: Numeric, units: UnitPrefs): number | null {
  const n = toNumber(canonical);
  if (n == null) return null;
  if (isElectric(commodity)) return n * electricRateSpec(units.electricRate).factor;
  if (isGas(commodity)) return n * gasSpec(units.gasRate).thermsPerUnit;
  return n;
}

/** The reader's rate unit: `$/kWh`, `¢/kWh`, `$/MWh`, `$/therm`, `$/Dth`, … */
export function rateUnitLabel(commodity: Commodity, units: UnitPrefs): string {
  if (isElectric(commodity)) return electricRateSpec(units.electricRate).label;
  if (isGas(commodity)) return gasSpec(units.gasRate).rateLabel;
  return "";
}

/** The denominator alone: `kWh`, `MWh`, `therm`, `Dth`, … */
export function rateDenominatorLabel(commodity: Commodity, units: UnitPrefs): string {
  return rateUnitLabel(commodity, units).replace(/^[$¢]\//, "");
}

/** Decimals the reader shows this commodity's rates at. */
export function ratePrecision(commodity: Commodity, units: UnitPrefs): number {
  return isGas(commodity) ? units.gasRatePrecision : units.electricRatePrecision;
}

/** A canonical rate without its unit: `$0.0739`, `7.39¢`, `$73.90`. `—` for no value. */
export function formatRateBare(commodity: Commodity, canonical: Numeric, units: UnitPrefs): string {
  const value = rateValue(commodity, canonical, units);
  if (value == null) return "—";
  return formatRateNumber(value, ratePrecision(commodity, units), rateInCents(commodity, units));
}

/** A canonical rate with its unit: `$0.0739/kWh`, `7.39¢/kWh`, `$5.1200/Dth`. `—` for no value. */
export function formatRate(commodity: Commodity, canonical: Numeric, units: UnitPrefs): string {
  const bare = formatRateBare(commodity, canonical, units);
  if (bare === "—") return bare;
  const denominator = rateDenominatorLabel(commodity, units);
  return denominator ? `${bare}/${denominator}` : bare;
}

/**
 * A rate typed in the reader's unit, as the canonical rate (`$/kWh`,
 * `$/therm`) — the inverse of `rateValue`, for an input that takes the
 * reader's unit. Water and other pass through.
 */
export function rateToCanonicalValue(commodity: Commodity, shown: Numeric, units: UnitPrefs): number | null {
  const n = toNumber(shown);
  if (n == null) return null;
  const perUnit = rateValue(commodity, 1, units);
  return perUnit ? n / perUnit : n;
}

/**
 * Whether the reader's rates of this commodity print in cents (`7.39¢`)
 * rather than dollars — the one thing `formatRateNumber` needs to know.
 */
export function rateInCents(commodity: Commodity, units: UnitPrefs): boolean {
  return isElectric(commodity) && units.electricRate === "cents_per_kwh";
}

/**
 * A number ALREADY in the reader's rate unit, at `dp` decimals: `$0.0739`, or
 * `7.39¢` when `cents`. For a chart that plots `rateValue`s and an axis that
 * rounds harder than the reader's precision; anything holding the canonical
 * value formats that with `formatRateBare` instead. `—` for no value.
 */
export function formatRateNumber(value: number | null | undefined, dp: number, cents: boolean): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return cents ? `${grouped(value, dp)}¢` : dollars(value, dp);
}

// ── Entered units ─────────────────────────────────────────────────────────

/**
 * Canonical units (kWh or therms) in one of an ENTERED unit — the codes a
 * per-field unit picker stores (`deals.margin_uom`, `price_uom`, a block's
 * unit): kWh, MWh, therm, Ccf, Dth, Mcf, MMBtu, on the volumetric gas
 * convention.
 */
const CANONICAL_PER_ENTERED: Record<string, number> = {
  kwh: 1,
  mwh: 1000,
  therm: 1,
  therms: 1,
  thm: 1,
  ccf: 1,
  dth: 10,
  mcf: 10,
  mmbtu: 10,
};

/**
 * A rate entered per `uom` (`$79.12` per MWh) as the canonical rate
 * (`$0.07912/kWh`) — what `formatRate` and `useUnits().rate` take. A value
 * with no unit, or one this doesn't know, is taken as canonical already.
 */
export function canonicalRate(value: Numeric, uom: string | null | undefined): number | null {
  const n = toNumber(value);
  if (n == null) return null;
  const per = uom ? CANONICAL_PER_ENTERED[uom.trim().toLowerCase()] : undefined;
  return per ? n / per : n;
}

/**
 * A stored usage unit as its catalog code: a block saved before the unit
 * catalog reads `kWh` or `therm`, which a picker keyed on the codes
 * (`kwh`, `therms`) would not match. A unit outside the catalog comes back
 * as stored; none comes back empty.
 */
export function usageUnitCode(stored: string | null | undefined): string {
  const key = (stored ?? "").trim().toLowerCase();
  if (key === "therm" || key === "thm") return "therms";
  return key in CANONICAL_PER_ENTERED ? key : (stored ?? "");
}

/** A usage entered in `uom` (`500` MWh) as canonical usage (`500,000` kWh). */
export function canonicalUsage(value: Numeric, uom: string | null | undefined): number | null {
  const n = toNumber(value);
  if (n == null) return null;
  const per = uom ? CANONICAL_PER_ENTERED[uom.trim().toLowerCase()] : undefined;
  return per ? n * per : n;
}

// ── Usage ─────────────────────────────────────────────────────────────────

/** A canonical usage in the reader's usage unit, unrounded. */
export function usageValue(commodity: Commodity, canonical: Numeric, units: UnitPrefs): number | null {
  const n = toNumber(canonical);
  if (n == null) return null;
  if (isElectric(commodity)) return n / electricUsageSpec(units.electricUsage).kwhPerUnit;
  if (isGas(commodity)) return n / gasSpec(units.gasUsage).thermsPerUnit;
  return n;
}

/**
 * A usage typed in the reader's unit, as canonical usage (kWh electric,
 * therms gas) — the inverse of `usageValue`, for an input that takes the
 * reader's unit. Water and other pass through.
 */
export function usageToCanonical(commodity: Commodity, shown: Numeric, units: UnitPrefs): number | null {
  const n = toNumber(shown);
  if (n == null) return null;
  const perUnit = usageValue(commodity, 1, units);
  return perUnit ? n / perUnit : n;
}

/**
 * Decimals a usage typed in the reader's unit can carry before it goes finer
 * than one canonical unit: MWh 3 (a kWh), Dth / Mcf / MMBtu 1 (a therm), kWh,
 * therms and Ccf 0. An input caps at this, never at the display precision: a
 * reader who reads MWh at 0 decimals still types `2.5`.
 */
export function usageEntryDecimals(commodity: Commodity, units: UnitPrefs): number {
  const perUnit = usageToCanonical(commodity, 1, units) ?? 1;
  return Math.max(0, Math.round(Math.log10(perUnit)));
}

/** The reader's usage unit: `kWh`, `MWh`, `therms`, `Dth`, … */
export function usageUnitLabel(commodity: Commodity, units: UnitPrefs): string {
  if (isElectric(commodity)) return electricUsageSpec(units.electricUsage).label;
  if (isGas(commodity)) return gasSpec(units.gasUsage).usageLabel;
  return "";
}

/** Decimals the reader shows this commodity's usage at. */
export function usagePrecision(commodity: Commodity, units: UnitPrefs): number {
  if (isElectric(commodity)) return units.electricUsagePrecision;
  if (isGas(commodity)) return units.gasUsagePrecision;
  return 0;
}

/** A canonical usage without its unit: `12,000`. `—` for no value. */
export function formatUsageBare(commodity: Commodity, canonical: Numeric, units: UnitPrefs): string {
  const value = usageValue(commodity, canonical, units);
  return value == null ? "—" : grouped(value, usagePrecision(commodity, units));
}

/** A canonical usage with its unit: `12,000 kWh`, `1,200.0 Mcf`. `—` for no value. */
export function formatUsage(commodity: Commodity, canonical: Numeric, units: UnitPrefs): string {
  const bare = formatUsageBare(commodity, canonical, units);
  if (bare === "—") return bare;
  const unit = usageUnitLabel(commodity, units);
  return unit ? `${bare} ${unit}` : bare;
}

// ── The page's units ──────────────────────────────────────────────────────

let active: UnitPrefs = DEFAULT_UNIT_PREFS;

/**
 * The signed-in reader's units, for code with no React to hook into (a chart
 * axis formatter, a CSV builder). Set by `AppShell` before any page renders,
 * and the shell re-renders (and, on a unit change, remounts the page) when
 * they change — so a read here is current. A component should still prefer
 * `useUnits()`.
 */
export function getActiveUnits(): UnitPrefs {
  return active;
}

/** Called by `AppShell` only. */
export function setActiveUnits(units: UnitPrefs): void {
  active = units;
}

/**
 * A stable string for the reader's unit choices, for a key that remounts the
 * page when one changes. Decimals are left out: they change most often, and
 * the shell's own re-render repaints them in place, so a page's unsaved form
 * edits survive a decimals click.
 */
export function unitPrefsKey(units: UnitPrefs): string {
  return [units.electricRate, units.electricUsage, units.gasRate, units.gasUsage].join(":");
}

// ── Bound to one reader ───────────────────────────────────────────────────

/** Every unit operation, bound to one reader's units — what `useUnits()` returns. */
export interface Units {
  prefs: UnitPrefs;
  /** `$0.0739/kWh` */
  rate: (commodity: Commodity, canonical: Numeric) => string;
  /** `$0.0739` */
  rateBare: (commodity: Commodity, canonical: Numeric) => string;
  rateValue: (commodity: Commodity, canonical: Numeric) => number | null;
  /** `$/kWh` */
  rateUnit: (commodity: Commodity) => string;
  /** `kWh` — to follow a bare rate: `${units.rateBare(c, v)}/${units.rateDenominator(c)}` */
  rateDenominator: (commodity: Commodity) => string;
  ratePrecision: (commodity: Commodity) => number;
  /** `12,000 kWh` */
  usage: (commodity: Commodity, canonical: Numeric) => string;
  /** `12,000` */
  usageBare: (commodity: Commodity, canonical: Numeric) => string;
  usageValue: (commodity: Commodity, canonical: Numeric) => number | null;
  /** `kWh` */
  usageUnit: (commodity: Commodity) => string;
  usagePrecision: (commodity: Commodity) => number;
}

/** Binds every unit operation to `prefs`. */
export function bindUnits(prefs: UnitPrefs): Units {
  return {
    prefs,
    rate: (c, v) => formatRate(c, v, prefs),
    rateBare: (c, v) => formatRateBare(c, v, prefs),
    rateValue: (c, v) => rateValue(c, v, prefs),
    rateUnit: (c) => rateUnitLabel(c, prefs),
    rateDenominator: (c) => rateDenominatorLabel(c, prefs),
    ratePrecision: (c) => ratePrecision(c, prefs),
    usage: (c, v) => formatUsage(c, v, prefs),
    usageBare: (c, v) => formatUsageBare(c, v, prefs),
    usageValue: (c, v) => usageValue(c, v, prefs),
    usageUnit: (c) => usageUnitLabel(c, prefs),
    usagePrecision: (c) => usagePrecision(c, prefs),
  };
}
